export var ScheduleType;
(function (ScheduleType) {
    ScheduleType["GROUP"] = "GROUP";
    ScheduleType["INDIVIDUAL"] = "INDIVIDUAL";
})(ScheduleType || (ScheduleType = {}));
export var RecurrencyType;
(function (RecurrencyType) {
    RecurrencyType["SINGLE"] = "SINGLE";
    RecurrencyType["WEEKLY"] = "WEEKLY";
})(RecurrencyType || (RecurrencyType = {}));
export var DayOfWeek;
(function (DayOfWeek) {
    DayOfWeek["MONDAY"] = "MONDAY";
    DayOfWeek["TUESDAY"] = "TUESDAY";
    DayOfWeek["WEDNESDAY"] = "WEDNESDAY";
    DayOfWeek["THURSDAY"] = "THURSDAY";
    DayOfWeek["FRIDAY"] = "FRIDAY";
    DayOfWeek["SATURDAY"] = "SATURDAY";
    DayOfWeek["SUNDAY"] = "SUNDAY";
})(DayOfWeek || (DayOfWeek = {}));
export var ScheduleSessionAttendanceStatus;
(function (ScheduleSessionAttendanceStatus) {
    ScheduleSessionAttendanceStatus["CONFIRMED"] = "CONFIRMED";
    ScheduleSessionAttendanceStatus["CANCELED"] = "CANCELED";
    ScheduleSessionAttendanceStatus["TRIAL"] = "TRIAL";
    ScheduleSessionAttendanceStatus["REPOSITION"] = "REPOSITION";
    ScheduleSessionAttendanceStatus["PAUSED"] = "PAUSED";
})(ScheduleSessionAttendanceStatus || (ScheduleSessionAttendanceStatus = {}));
// Session type for single session scheduling (Trial, Reposition, One Time Session)
export var SingleSessionType;
(function (SingleSessionType) {
    SingleSessionType["TRIAL"] = "TRIAL";
    SingleSessionType["REPOSITION"] = "REPOSITION";
    SingleSessionType["ONE_TIME_SESSION"] = "ONE_TIME_SESSION";
})(SingleSessionType || (SingleSessionType = {}));
// How a blocking enrollment is resolved when its schedule is archived (D2).
export var ArchiveResolutionAction;
(function (ArchiveResolutionAction) {
    ArchiveResolutionAction["MOVE"] = "MOVE";
    ArchiveResolutionAction["CANCEL"] = "CANCEL";
    ArchiveResolutionAction["PAUSE"] = "PAUSE";
})(ArchiveResolutionAction || (ArchiveResolutionAction = {}));
// Helper types for display labels
export const SCHEDULE_TYPE_LABELS = {
    [ScheduleType.GROUP]: "Group",
    [ScheduleType.INDIVIDUAL]: "Individual",
};
export const RECURRENCY_TYPE_LABELS = {
    [RecurrencyType.SINGLE]: "Single Session",
    [RecurrencyType.WEEKLY]: "Weekly",
};
export const DAY_OF_WEEK_LABELS = {
    [DayOfWeek.MONDAY]: "Monday",
    [DayOfWeek.TUESDAY]: "Tuesday",
    [DayOfWeek.WEDNESDAY]: "Wednesday",
    [DayOfWeek.THURSDAY]: "Thursday",
    [DayOfWeek.FRIDAY]: "Friday",
    [DayOfWeek.SATURDAY]: "Saturday",
    [DayOfWeek.SUNDAY]: "Sunday",
};
// Separator between label segments, e.g. "Grupo · Maria · Seg, Qua 09:00".
const SCHEDULE_LABEL_SEPARATOR = " · ";
// Build a `Date` at LOCAL midnight for the given calendar day. The injected
// `strings.formatDate` is a locale formatter that reads local getFullYear/
// getMonth/getDate; constructing from parts (never `new Date("YYYY-MM-DD")`,
// which is UTC midnight and shifts west of Greenwich) makes it render the
// intended day in every zone.
const calendarDateToLocalDate = (date) => new Date(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)));
// Pure, side-effect-free formatter — the single source of truth for every
// human-facing Schedule label. Callers inject all localized strings, so this
// function is fully i18n-agnostic.
//
//   full:    `${typeLabel} · ${staffFirstName} · ${days|date} ${startTime}`
//   compact: `${typeLabel} · ${staffFirstName}`  (drops day/time)
//
// A WEEKLY schedule renders its day list in canonical weekday order; a SINGLE
// schedule (or any session carrying a `sessionDate`) renders the concrete date
// instead. Missing days/date degrade gracefully — no crash, no dangling
// separators.
export function formatScheduleLabel(inputs, strings, options) {
    const head = [strings.typeLabel, inputs.staffFirstName]
        .filter((part) => part.length > 0)
        .join(SCHEDULE_LABEL_SEPARATOR);
    if (options?.compact) {
        return head;
    }
    const when = inputs.recurrencyType === RecurrencyType.SINGLE
        ? inputs.sessionDate !== undefined
            ? strings.formatDate(calendarDateToLocalDate(inputs.sessionDate))
            : ""
        : sortDaysByWeekOrder(inputs.daysOfWeek)
            .map((day) => strings.dayAbbreviations[day])
            .join(", ");
    // Join the day/date and start time with a single space, dropping either part
    // when it is empty so no leading/trailing whitespace survives.
    const scheduleTiming = [when, inputs.startTime].filter(Boolean).join(" ");
    return [head, scheduleTiming]
        .filter((part) => part.length > 0)
        .join(SCHEDULE_LABEL_SEPARATOR);
}
// Map a full `Schedule` onto the structural inputs `formatScheduleLabel`
// consumes. Single source of truth for the Schedule → label-inputs projection so
// every labeled surface derives the same fields the same way.
export const scheduleToLabelInputs = (schedule) => ({
    type: schedule.type,
    staffFirstName: schedule.staffMember?.firstName ?? "",
    startTime: schedule.startTime,
    recurrencyType: schedule.recurrencyType,
    daysOfWeek: schedule.daysOfWeek,
    sessionDate: schedule.recurrencyType === RecurrencyType.SINGLE
        ? schedule.startDate
        : undefined,
});
// Helper function to get schedule display name.
// Delegates to `formatScheduleLabel` (full form) using the package's built-in
// English label constants as the default localization. Language-aware callers
// resolve strings via i18n and call `formatScheduleLabel` directly.
export const getScheduleDisplayName = (schedule) => {
    return formatScheduleLabel(scheduleToLabelInputs(schedule), {
        typeLabel: SCHEDULE_TYPE_LABELS[schedule.type],
        dayAbbreviations: DAY_OF_WEEK_LABELS,
        formatDate: (d) => d.toLocaleDateString(),
    });
};
// Helper function to format schedule time
export const formatScheduleTime = (startTime, endTime) => {
    // Handle both string (HH:MM) and Date formats for backward compatibility
    let start;
    let end;
    if (typeof startTime === "string") {
        // Already in HH:MM format
        start = startTime;
    }
    else {
        // Convert Date to HH:MM format
        start = startTime.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    }
    if (typeof endTime === "string") {
        // Already in HH:MM format
        end = endTime;
    }
    else {
        // Convert Date to HH:MM format
        end = endTime.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    }
    return `${start} - ${end}`;
};
// Canonical weekday order used to render day lists deterministically
// (Monday → Sunday). Single source of truth for every day-list formatter.
const WEEKDAY_ORDER = [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
    DayOfWeek.SUNDAY,
];
// Sort a copy of the given days into canonical weekday order without mutating
// the caller's array.
const sortDaysByWeekOrder = (daysOfWeek) => [...daysOfWeek].sort((a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b));
// Helper function to format days of week
export const formatDaysOfWeek = (daysOfWeek) => {
    if (daysOfWeek.length === 0)
        return "No days selected";
    if (daysOfWeek.length === 7)
        return "Every day";
    return sortDaysByWeekOrder(daysOfWeek)
        .map((day) => DAY_OF_WEEK_LABELS[day])
        .join(", ");
};
export const getStaffMemberDisplayName = (staffMember) => {
    if (!staffMember)
        return "Unknown";
    return `${staffMember.firstName} ${staffMember.lastName}`.trim();
};
//# sourceMappingURL=Schedule.js.map